import React, { useEffect, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ButtonBase from '@mui/material/ButtonBase';
import Fade from '@mui/material/Fade';
import { alpha, type Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const edgeFade = (theme: Theme, toward: 'top' | 'bottom') =>
  `linear-gradient(to ${toward}, ${alpha(theme.palette.background.paper, 0)} 8.333%, ${theme.palette.background.paper} 100%)`;

const useStyles = makeStyles<void, 'pill'>()((theme, _, classes) => ({
  strip: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1,
    boxSizing: 'border-box',
    height: theme.spacing(3),
    padding: theme.spacing(0.5, 0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [`&:hover .${classes.pill}`]: {
      backgroundColor: theme.palette.secondary.light,
      color: theme.palette.primary.dark,
    },
  },
  up: { top: 0, background: edgeFade(theme, 'top') },
  down: { bottom: 0, background: edgeFade(theme, 'bottom') },
  pill: {
    display: 'flex',
    padding: theme.spacing(0, 1.5),
    borderRadius: theme.shape.radius.md,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
  },
  icon: { fontSize: theme.spacing(2) },
}));

type NavScrollArrowProps = {
  direction: 'up' | 'down';
  scrollParent: HTMLElement | null;
};

export const NavScrollArrow = React.memo(function NavScrollArrow({
  direction,
  scrollParent,
}: NavScrollArrowProps): React.JSX.Element {
  const { classes, cx, theme } = useStyles();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!scrollParent) {
      return undefined;
    }
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollParent;
      setVisible(direction === 'up' ? scrollTop > 1 : scrollHeight - clientHeight - scrollTop > 1);
    };
    update();
    scrollParent.addEventListener('scroll', update, { passive: true });
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    resizeObserver?.observe(scrollParent);
    if (scrollParent.firstElementChild) {
      resizeObserver?.observe(scrollParent.firstElementChild);
    }
    return () => {
      scrollParent.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
    };
  }, [direction, scrollParent]);

  const Icon = direction === 'up' ? ExpandLessIcon : ExpandMoreIcon;

  return (
    <Fade in={visible} timeout={theme.transitions.duration.shortest} unmountOnExit>
      <ButtonBase
        aria-hidden
        tabIndex={-1}
        data-direction={direction}
        onClick={() =>
          scrollParent?.scrollBy({
            top: (direction === 'up' ? -0.8 : 0.8) * scrollParent.clientHeight,
            behavior: 'smooth',
          })
        }
        className={cx(classes.strip, direction === 'up' ? classes.up : classes.down)}
      >
        <span className={classes.pill}>
          <Icon className={classes.icon} />
        </span>
      </ButtonBase>
    </Fade>
  );
});
